import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import Detail from '../../../../../types/detail/Detail';
import SampleCategory from '../../../../../types/sample-category/SampleCategory';
import { CanManageSampleDefinitionsDirective } from '../../../directives/can-manage-sample-definitions.directive';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { DetailService } from '../../../services/detail.service';
import { SampleCategoryService } from '../../../services/sample-category.service';
import {
    AddDetailsToCategoryDialogComponent
} from '../../shared/details/add-details-to-category-dialog/add-details-to-category-dialog.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import {
    UpdateSampleCategoryDialogComponent
} from '../../shared/sample-categories/update-sample-category-dialog/update-sample-category-dialog.component';

@Component({
    selector: 'app-sample-category-detail',
    templateUrl: './sample-category-detail.component.html',
    styleUrls: [ './sample-category-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateSampleCategoryDialogComponent,
        SharedModule,
        TableModule,
        AddDetailsToCategoryDialogComponent,
        Tooltip,
        RoleNamePipe,
        CanManageSampleDefinitionsDirective
    ]
})
export class SampleCategoryDetailComponent implements OnInit {
    public sampleCategory: SampleCategory | null = null;
    public editableSampleCategory: SampleCategory = new SampleCategory({});
    public displayUpdateDialog = false;
    public details: Detail[] = [];
    public displayAddDetailsDialog: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private sampleCategoryService: SampleCategoryService,
        private detailsService: DetailService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public async ngOnInit(): Promise<void> {
        await this.loadSampleCategory();
    }

    /**
     * Opens the update dialog by creating an editable copy of the sample category.
     */
    public openEditDialog(): void {
        this.editableSampleCategory = new SampleCategory(this.sampleCategory ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the sample category details (for example after an update).
     */
    public async reloadSampleCategoryDetails(): Promise<void> {
        await this.loadSampleCategory();
    }

    /**
     * Confirms deletion of the sample category.
     */
    public confirmDelete(): void {
        if (!this.sampleCategory) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the sample category '${ this.sampleCategory.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await this.sampleCategoryService.deleteSampleCategory(this.sampleCategory!.id);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Sample category deleted successfully',
                        life: 3000
                    });
                    // Navigate back (for example, to the list of sample categories)
                    window.history.back();
                } catch (error: any) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000
                    });
                }
            }
        });
    }

    /**
     * Loads the details for the sample category.
     */
    public loadDetails(): void {
        this.detailsService.getDetailsByCategory(this.sampleCategory!.id).then((details) => {
            this.details = details;
        }).catch((error) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    /**
     * Loads the sample category details based on the route parameter.
     */
    private async loadSampleCategory(): Promise<void> {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            try {
                this.sampleCategory = await this.sampleCategoryService.getSampleCategory(idNumber);
                this.loadDetails();
            } catch (error: any) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            }
        }
    }

    /**
     * Removes a detail from the sample category.
     *
     * @param detail The detail to remove.
     */
    public removeDetail(detail: Detail) {
        this.detailsService.removeCategoryFromDetail(detail.id, this.sampleCategory!.id).then(() => {
            this.loadDetails();
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Detail removed',
                life: 3000
            });
        }).catch((error) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public openAddDetailsDialog() {
        this.displayAddDetailsDialog = true;
    }
}
